// Chicken behaviour state machine. Birds act on what they need: thirsty birds
// head for water, dusk sends everyone to roost, rain sends them under cover.
// Replaces the Phase 1 wander placeholder.

import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import type { ChickenEntity, ChickenState, GameState, Structure } from '../core/types';
import { isExcluded, stationPressure } from './access';
import { getConfig, type BiologyConfig } from './config';
import { dailyBroodyChance } from './genetics';
import { effectiveTempForSpot, openSpotsByCoolth } from './microclimate';
import { spotDefs, structureInSpot, type SpotDef } from './spots';
import { hourOfDay } from './time';
import { layoutStructures } from './structureLayout';
import { availableFeed, availableWater, type FacilitySummary } from './welfare';

/** Dusk pulls birds to roost; this is also when the coop must be locked. */
export function isRoostingTime(state: GameState): boolean {
  const hour = hourOfDay(state.simHour);
  return hour >= state.weather.sunsetHour - 0.5 || hour < state.weather.sunriseHour;
}

/** Equipment has no player-chosen position; it lives at its fixed anchor. */
function structureTile(state: GameState, structure: Structure): { x: number; y: number } {
  return layoutStructures(state).get(structure.id) ?? { x: 0, y: 0 };
}

/** One step toward a target tile — birds walk, they do not teleport. */
function stepToward(
  chicken: ChickenEntity,
  target: { x: number; y: number },
  state: GameState,
): boolean {
  const dx = Math.sign(target.x - chicken.position.x);
  const dy = Math.sign(target.y - chicken.position.y);
  if (dx === 0 && dy === 0) return true;
  const nx = clamp(chicken.position.x + dx, 0, state.grid.width - 1);
  const ny = clamp(chicken.position.y + dy, 0, state.grid.height - 1);
  chicken.position.x = nx;
  chicken.position.y = ny;
  return nx === target.x && ny === target.y;
}

/**
 * Tiles a bird covers in one behaviour tick. The floor of one keeps live play
 * walking at the pace it always did; only a step longer than a quarter of an
 * hour buys more ground.
 */
function walkBudget(config: BiologyConfig, hours: number): number {
  return Math.max(1, Math.floor(hours * config.spots.walkTilesPerHour));
}

/**
 * Walks toward a target for as long as the step being simulated lasted.
 *
 * Behaviour runs once per sim step, so a step that covers three sim-hours used
 * to buy a bird one tile. That is fine at 1 Hz and wrong on catch-up: a hot
 * bird with a shaded spot three tiles away burned six hours of lethal heat
 * before she reached it and died of heat stroke in a yard that had shade in it.
 */
function advanceToward(
  chicken: ChickenEntity,
  target: { x: number; y: number },
  state: GameState,
  config: BiologyConfig,
  hours: number,
): boolean {
  for (let i = 0, n = walkBudget(config, hours); i < n; i++) {
    if (stepToward(chicken, target, state)) return true;
  }
  return false;
}

/**
 * Records which fixed spot the bird has reached (PLAN-04 S3). The spot is what
 * decides the temperature she is carrying, so standing somewhere is not the same
 * as standing nowhere.
 */
function arriveAt(chicken: ChickenEntity, state: GameState, arrived: boolean): ChickenState {
  if (arrived) {
    const spot = spotAt(state, chicken.position.x, chicken.position.y);
    // A spot with gear in it is the one she walked to; an empty tile only claims
    // a bird that is not already at one.
    if (spot && (structureInSpot(state, spot.id) || chicken.spotId === undefined)) {
      chicken.spotId = spot.id;
    }
  }
  return arrived ? 'PECKING' : 'WALKING';
}

/**
 * The fixed spot a tile belongs to. Two spots can share a world anchor (a yard
 * spot and the inside spot behind it), so the yard wins: it is the one a bird
 * walking there is actually standing in.
 */
function spotAt(state: GameState, x: number, y: number): SpotDef | undefined {
  const spots = spotDefs(state.housing);
  return (
    spots.find((s) => s.kind === 'yard' && s.world.x === x && s.world.y === y) ??
    spots.find((s) => s.kind === 'inside' && s.world.x === x && s.world.y === y)
  );
}

/** Where a hot bird is heading, and whether she can get there this step. */
export interface ShadeEscape {
  spot: SpotDef;
  /** The air she will be carrying once she is standing there. */
  tempF: number;
  /**
   * True when this step's walk budget covers the distance. A bird cannot be
   * credited with shade she will not reach before the step ends, which is the
   * difference between a live tick (one tile) and a catch-up step (many).
   */
  reachable: boolean;
}

/**
 * Out of the sun. A bird carrying more than the heat-load threshold (PHY-17)
 * walks to a cool open spot and stays off the open ground while it is cooler
 * there. This is the movement half of the first visible heat sign; S7 hangs the
 * sign itself on the same comparison.
 *
 * The answer is computed before physiology runs, so both agree on which spot
 * this bird is heading for. See `simulate.ts` for why the step needs it in
 * advance.
 */
export function shadeEscape(
  chicken: ChickenEntity,
  state: GameState,
  config: BiologyConfig,
  hours: number,
): ShadeEscape | null {
  // A bird that is in for the night does not get up to find shade: the coop is
  // where a roosting bird is, whatever the roof is doing to it.
  if (chicken.currentState === 'ROOSTING') return null;
  const hour = hourOfDay(state.simHour);
  const here = effectiveTempForSpot(state, chicken.spotId, hour, config);
  if (here < config.spots.shadeSeekTempF) return null;

  const choices = openSpotsByCoolth(state, hour, config);
  const coolest = choices[0];
  if (!coolest) return null;
  const there = coolest.tempF;
  if (there >= here) return null;

  // Spread the flock across the shade rather than stacking it on one tile.
  // Pecking order decides who gets the first choice, which is also the
  // deterministic way to do it: a replay puts the same bird in the same spot.
  const spot = choices[chicken.hierarchyRank % choices.length].spot;
  const distance =
    Math.abs(spot.world.x - chicken.position.x) + Math.abs(spot.world.y - chicken.position.y);
  return { spot, tempF: there, reachable: distance <= walkBudget(config, hours) };
}

function shadeStep(
  chicken: ChickenEntity,
  state: GameState,
  config: BiologyConfig,
  hours: number,
  escape: ShadeEscape | null,
): ChickenState | null {
  if (!escape) return null;
  const arrived = advanceToward(chicken, escape.spot.world, state, config, hours);
  if (arrived) chicken.spotId = escape.spot.id;
  return arrived ? 'IDLE' : 'WALKING';
}

export interface BehaviorContext {
  facilities: FacilitySummary;
  config: BiologyConfig;
  rng: Rng;
  /** Sim-hours this step covers, so a bird walks for the time that passed. */
  hours: number;
  /**
   * Where this bird is heading for shade this step, already decided. Passing it
   * in keeps the walk and the temperature she is charged for pointing at the
   * same tile.
   */
  escape: ShadeEscape | null;
}

/**
 * Decides and applies one bird's behaviour for this step. Needs beat comfort:
 * a parched bird walks to water before it thinks about roosting.
 */
export function stepBehavior(
  chicken: ChickenEntity,
  state: GameState,
  context: BehaviorContext,
): ChickenState {
  const { facilities, rng } = context;

  // Escaped birds are off-grid until the player retrieves them.
  if (chicken.currentState === 'ESCAPED') return 'ESCAPED';

  const p = chicken.physiology;

  // 1. Thirst first — it is the fastest danger and the strongest drive.
  const water = availableWater(facilities);
  if (p.hydration < 55 && water.has && facilities.waterers.length > 0) {
    return arriveAt(chicken, state, stepToward(chicken, structureTile(state, facilities.waterers[0]), state));
  }

  // 2. Hunger.
  const feed = availableFeed(facilities, chicken);
  if (p.energyReserve < 55 && feed.has && facilities.feeders.length > 0) {
    return arriveAt(chicken, state, stepToward(chicken, structureTile(state, facilities.feeders[0]), state));
  }

  // 3. Out of the sun. Below a parched bird but above a pottering one, because
  //    heat does not wait for hunger and a drinker in the sun is the worst place
  //    in the yard to be (PLAN-04 S3, PHY-17).
  const shaded = shadeStep(chicken, state, context.config, context.hours, context.escape);
  if (shaded) return shaded;

  // 4. Shelter from rain and storms.
  const wet = state.weather.condition === 'RAIN' || state.weather.condition === 'STORM';
  if (wet && facilities.coops.length > 0) {
    const arrived = stepToward(chicken, structureTile(state, facilities.coops[0]), state);
    chicken.currentState = arrived ? 'ROOSTING' : 'WALKING';
    return chicken.currentState;
  }

  // 5. Dusk: everyone goes in. Chickens do this by themselves — the player's
  //    job is to shut the door behind them.
  if (isRoostingTime(state)) {
    const target = facilities.perches[0] ?? facilities.coops[0] ?? facilities.brooders[0];
    if (target) {
      const arrived = stepToward(chicken, structureTile(state, target), state);
      chicken.currentState = arrived ? 'ROOSTING' : 'WALKING';
      return chicken.currentState;
    }
    chicken.currentState = 'ROOSTING';
    return chicken.currentState;
  }

  // 6. A broody hen sits on the nest and stops doing much else.
  if (
    chicken.currentState === 'BROODY' ||
    (chicken.gender === 'hen' &&
      chicken.stage === 'adult' &&
      facilities.nestBoxes.length > 0 &&
      rng.chance(dailyBroodyChance(chicken.genotype) / 24))
  ) {
    const arrived = stepToward(chicken, structureTile(state, facilities.nestBoxes[0]), state);
    chicken.currentState = arrived ? 'BROODY' : 'WALKING';
    return chicken.currentState;
  }

  // 7. Otherwise: potter about. Stressed birds move less.
  const activity = 0.3 * (1 - p.stress / 200);
  if (rng.chance(activity)) {
    const dx = rng.int(-1, 1);
    const dy = rng.int(-1, 1);
    chicken.position.x = clamp(chicken.position.x + dx, 0, state.grid.width - 1);
    chicken.position.y = clamp(chicken.position.y + dy, 0, state.grid.height - 1);
    chicken.currentState = dx !== 0 || dy !== 0 ? 'WALKING' : 'PECKING';
  } else {
    chicken.currentState = rng.chance(0.4) ? 'PECKING' : 'IDLE';
  }
  return chicken.currentState;
}

/**
 * Pecking order. Older, healthier birds rank higher; low-rank birds get blocked
 * at a single feeder, which is exactly why a second feeder is real advice.
 */
export function recalculateHierarchy(state: GameState): void {
  const ranked = [...state.flock].sort((a, b) => {
    const scoreA = a.ageInDays + a.physiology.bodyCondition;
    const scoreB = b.ageInDays + b.physiology.bodyCondition;
    return scoreB - scoreA;
  });
  ranked.forEach((chicken, index) => {
    chicken.hierarchyRank = index;
  });
}

/**
 * True when this bird is one of the ones the yard's feeder layout keeps off the
 * feed. BHV-17 is explicit that a single watched trough excludes the bottom of
 * the order rather than merely slowing it down — which is why a second feeder
 * is real advice. The pressure itself is `sim/access.ts`, and this is the same
 * question the hourly roll asks, so the HUD and the sim cannot disagree.
 */
export function isBlockedFromFeeder(
  chicken: ChickenEntity,
  state: GameState,
  facilities: FacilitySummary,
): boolean {
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  void facilities;
  return isExcluded(
    chicken,
    stationPressure(state, 'feed', config),
    state.flock.length,
    config,
  );
}
